import React, { useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { GeoJsonPolygon, GeoJsonPoint, OmgevingsData, VoorzieningItem } from '../../types';
import { OmgevingsDataLayer } from './OmgevingsDataLayer';
import { GebiedsdataLayer } from './GebiedsdataLayer';
import { PandenLayer } from './PandenLayer';

// Fix Leaflet default icon issue with webpack/CRA
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom flag/pin icon for markers
const flagIcon = L.divIcon({
  html: `<svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 2V30" stroke="#E6007E" stroke-width="2" stroke-linecap="round"/>
    <path d="M3 2H20L16 9L20 16H3V2Z" fill="#E6007E" fill-opacity="0.9" stroke="#E6007E" stroke-width="1.5"/>
  </svg>`,
  className: 'custom-flag-icon',
  iconSize: [24, 32],
  iconAnchor: [3, 30],
  popupAnchor: [10, -28],
});

export interface MapMarker {
  id: string;
  position: GeoJsonPoint;
  label: string;
  type?: 'stakeholder' | 'poi' | 'flag' | 'custom';
}

interface AnalysisMapProps {
  onShapeCreated: (geojson: GeoJsonPolygon) => void;
  onShapeEdited?: (geojson: GeoJsonPolygon) => void;
  onMarkerAdded?: (position: GeoJsonPoint, label: string) => void;
  onMarkerRemoved?: (markerId: string) => void;
  boundary?: GeoJsonPolygon | null;
  bufferZone?: GeoJsonPolygon | null;
  markers?: MapMarker[];
  center?: [number, number];
  zoom?: number;
  omgevingsData?: OmgevingsData | null;
  activeFilters?: Record<string, boolean>;
  voorzieningen?: VoorzieningItem[] | null;
  bedrijvenOverpass?: VoorzieningItem[] | null;
  organisatieItems?: VoorzieningItem[] | null;
}

// Component to fly the map to a new center/zoom (e.g. after search)
const MapFlyTo: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();
  const prevCenter = useRef(center);
  const prevZoom = useRef(zoom);

  useEffect(() => {
    if (center[0] !== prevCenter.current[0] || center[1] !== prevCenter.current[1] || zoom !== prevZoom.current) {
      map.flyTo(center, zoom, { duration: 1.2 });
      prevCenter.current = center;
      prevZoom.current = zoom;
    }
  }, [center, zoom, map]);

  return null;
};

// Component to auto-fit the map to loaded boundary
const FitBounds: React.FC<{
  boundary?: GeoJsonPolygon | null;
  bufferZone?: GeoJsonPolygon | null;
}> = ({ boundary, bufferZone }) => {
  const map = useMap();
  const hasFitted = useRef(false);

  useEffect(() => {
    if (hasFitted.current) return;

    const geom = boundary || bufferZone;
    if (!geom) return;

    try {
      const geoJsonLayer = L.geoJSON({
        type: 'Feature',
        properties: {},
        geometry: geom,
      } as any);
      const bounds = geoJsonLayer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
        hasFitted.current = true;
      }
    } catch {
      // ignore invalid bounds
    }
  }, [boundary, bufferZone, map]);

  return null;
};

// Component that renders markers on the map
const MapMarkers: React.FC<{
  markers: MapMarker[];
  onMarkerRemoved?: (markerId: string) => void;
}> = ({ markers, onMarkerRemoved }) => {
  const map = useMap();
  const markerLayersRef = useRef<Map<string, L.Marker>>(new Map());

  useEffect(() => {
    // Remove markers that no longer exist
    markerLayersRef.current.forEach((layer, id) => {
      if (!markers.find(m => m.id === id)) {
        map.removeLayer(layer);
        markerLayersRef.current.delete(id);
      }
    });

    // Add/update markers
    markers.forEach(marker => {
      if (markerLayersRef.current.has(marker.id)) return;

      const [lng, lat] = marker.position.coordinates;
      const leafletMarker = L.marker([lat, lng], { icon: flagIcon })
        .addTo(map)
        .bindPopup(`
          <div style="font-family: 'Open Sans', sans-serif; min-width: 120px;">
            <strong style="font-size: 13px;">${marker.label}</strong>
            ${marker.type ? `<br/><span style="font-size: 11px; color: #9DA6A8; text-transform: uppercase;">${marker.type}</span>` : ''}
            ${onMarkerRemoved ? `<br/><button onclick="window.__removeMarker__('${marker.id}')" style="
              margin-top: 6px; padding: 3px 10px; font-size: 11px; color: #E6007E;
              border: 1px solid #E6007E; background: white; border-radius: 4px; cursor: pointer;
            ">Verwijderen</button>` : ''}
          </div>
        `);

      markerLayersRef.current.set(marker.id, leafletMarker);
    });
  }, [markers, map, onMarkerRemoved]);

  // Global callback for marker removal from popup
  useEffect(() => {
    if (onMarkerRemoved) {
      (window as any).__removeMarker__ = (id: string) => {
        onMarkerRemoved(id);
        map.closePopup();
      };
    }
    return () => {
      delete (window as any).__removeMarker__;
    };
  }, [onMarkerRemoved, map]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      markerLayersRef.current.forEach(layer => map.removeLayer(layer));
      markerLayersRef.current.clear();
    };
  }, [map]);

  return null;
};

// Component that adds Geoman drawing tools to the map
const GeomanControls: React.FC<{
  onShapeCreated: (geojson: GeoJsonPolygon) => void;
  onShapeEdited?: (geojson: GeoJsonPolygon) => void;
  onMarkerAdded?: (position: GeoJsonPoint, label: string) => void;
  boundary?: GeoJsonPolygon | null;
}> = ({ onShapeCreated, onShapeEdited, onMarkerAdded, boundary }) => {
  const map = useMap();
  const editableLayerRef = useRef<L.Layer | null>(null);

  // Use refs for callbacks to avoid re-initializing controls on every callback change
  const onShapeCreatedRef = useRef(onShapeCreated);
  const onShapeEditedRef = useRef(onShapeEdited);
  const onMarkerAddedRef = useRef(onMarkerAdded);
  useEffect(() => { onShapeCreatedRef.current = onShapeCreated; }, [onShapeCreated]);
  useEffect(() => { onShapeEditedRef.current = onShapeEdited; }, [onShapeEdited]);
  useEffect(() => { onMarkerAddedRef.current = onMarkerAdded; }, [onMarkerAdded]);

  // Render boundary as an editable Geoman layer
  const renderEditableBoundary = useCallback((geom: GeoJsonPolygon) => {
    // Remove old editable layer
    if (editableLayerRef.current) {
      map.removeLayer(editableLayerRef.current);
      editableLayerRef.current = null;
    }

    const layer = L.geoJSON({
      type: 'Feature',
      properties: {},
      geometry: geom,
    } as any, {
      style: {
        color: '#E6007E',
        weight: 3,
        fillColor: '#E6007E',
        fillOpacity: 0.2,
      },
    });

    layer.addTo(map);

    // Make each sub-layer editable via Geoman
    layer.eachLayer((subLayer: any) => {
      if (subLayer.pm) {
        subLayer.pm.enable();
        subLayer.pm.disable(); // Add to Geoman's internal layer list without starting edit mode

        // Listen for edits on this layer
        subLayer.on('pm:edit', () => {
          const editedGeoJson = subLayer.toGeoJSON();
          if (onShapeEditedRef.current) {
            onShapeEditedRef.current(editedGeoJson.geometry);
          }
        });
      }
    });

    editableLayerRef.current = layer;
  }, [map]);

  useEffect(() => {
    // Remove any existing controls first (handles StrictMode re-mount)
    map.pm.removeControls();
    map.off('pm:create');
    map.off('pm:remove');

    // Add Geoman controls
    map.pm.addControls({
      position: 'topright',
      drawMarker: true,
      drawCircleMarker: false,
      drawPolyline: false,
      drawText: false,
      drawCircle: true,
      drawRectangle: true,
      drawPolygon: true,
      editMode: true,
      dragMode: true,
      cutPolygon: false,
      removalMode: true,
      rotateMode: false,
    });

    // Style the drawn shapes
    map.pm.setGlobalOptions({
      pathOptions: {
        color: '#E6007E',
        fillColor: '#E6007E',
        fillOpacity: 0.15,
        weight: 2,
      },
    });

    // Listen for shape creation
    map.on('pm:create', (e: any) => {
      const layer = e.layer;

      // Handle marker creation
      if (e.shape === 'Marker') {
        const latlng = layer.getLatLng();
        map.removeLayer(layer);

        if (onMarkerAddedRef.current) {
          const label = prompt('Geef een naam voor deze markering:') || 'Markering';
          onMarkerAddedRef.current(
            { type: 'Point', coordinates: [latlng.lng, latlng.lat] },
            label
          );
        }
        return;
      }

      let geojson: any;

      if (e.shape === 'Circle') {
        // Convert circle to polygon approximation
        const center = layer.getLatLng();
        const radius = layer.getRadius();
        const points = 64;
        const coords: number[][] = [];

        for (let i = 0; i < points; i++) {
          const angle = (i / points) * 2 * Math.PI;
          const dx = radius * Math.cos(angle);
          const dy = radius * Math.sin(angle);
          const lat = center.lat + (dy / 111320);
          const lng = center.lng + (dx / (111320 * Math.cos(center.lat * Math.PI / 180)));
          coords.push([lng, lat]);
        }
        coords.push(coords[0]); // Close the ring

        geojson = {
          type: 'Polygon' as const,
          coordinates: [coords],
        };
      } else {
        const gj = layer.toGeoJSON();
        geojson = gj.geometry;
      }

      // Remove the drawn layer (we'll render it as editable)
      map.removeLayer(layer);
      onShapeCreatedRef.current(geojson);
    });

    // Listen for remove events from Geoman
    map.on('pm:remove', (e: any) => {
      if (editableLayerRef.current && e.layer === editableLayerRef.current) {
        editableLayerRef.current = null;
      }
    });

    return () => {
      map.pm.removeControls();
      map.off('pm:create');
      map.off('pm:remove');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Render boundary as editable layer when it changes
  useEffect(() => {
    if (boundary) {
      renderEditableBoundary(boundary);
    } else if (editableLayerRef.current) {
      map.removeLayer(editableLayerRef.current);
      editableLayerRef.current = null;
    }
  }, [boundary, renderEditableBoundary, map]);

  return null;
};

export const AnalysisMap: React.FC<AnalysisMapProps> = ({
  onShapeCreated,
  onShapeEdited,
  onMarkerAdded,
  onMarkerRemoved,
  boundary,
  bufferZone,
  markers = [],
  center = [52.1326, 5.2913], // Center of NL
  zoom = 8,
  omgevingsData = null,
  activeFilters = {},
  voorzieningen = null,
  bedrijvenOverpass = null,
  organisatieItems = null,
}) => {
  const bufferStyle = {
    color: '#E6007E',
    weight: 1,
    fillColor: '#E6007E',
    fillOpacity: 0.08,
    dashArray: '5, 10',
  };

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      style={{ height: '100%', width: '100%' }}
      zoomControl={true}
    >
      <LayersControl position="topleft">
        <LayersControl.BaseLayer checked name="BRT Achtergrondkaart">
          <TileLayer
            url="https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:3857/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.pdok.nl/">PDOK</a> / Kadaster'
            maxZoom={19}
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Luchtfoto">
          <TileLayer
            url="https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.jpeg"
            attribution='&copy; <a href="https://www.pdok.nl/">PDOK</a> / Beeldmateriaal.nl'
            maxZoom={19}
          />
        </LayersControl.BaseLayer>
      </LayersControl>

      <MapFlyTo center={center} zoom={zoom} />

      <GeomanControls
        onShapeCreated={onShapeCreated}
        onShapeEdited={onShapeEdited}
        onMarkerAdded={onMarkerAdded}
        boundary={boundary}
      />

      <FitBounds boundary={boundary} bufferZone={bufferZone} />

      <MapMarkers markers={markers} onMarkerRemoved={onMarkerRemoved} />

      {bufferZone && (
        <GeoJSON
          key={`buffer-${JSON.stringify(bufferZone).substring(0, 50)}`}
          data={{ type: 'Feature', properties: {}, geometry: bufferZone } as any}
          style={bufferStyle}
        />
      )}

      <OmgevingsDataLayer
        data={omgevingsData}
        activeFilters={activeFilters}
      />
      <GebiedsdataLayer
        voorzieningen={voorzieningen}
        bedrijven={bedrijvenOverpass}
        organisaties={organisatieItems}
        activeFilters={activeFilters}
      />
      <PandenLayer active={activeFilters.panden || false} />
    </MapContainer>
  );
};
